import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useGetPageHtmlQuery, LandingPage } from '../../api/pagesApi';

const PreviewOverlay = styled.div<{ show: boolean; mouseX: number; mouseY: number }>`
  position: fixed;
  top: ${props => Math.max(20, Math.min(props.mouseY - 150, window.innerHeight - 320))}px;
  left: ${props => Math.max(20, Math.min(props.mouseX + 20, window.innerWidth - 420))}px;
  width: 400px;
  height: 300px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  overflow: hidden;
  opacity: ${props => props.show ? 1 : 0};
  transform: ${props => props.show ? 'scale(1)' : 'scale(0.95)'};
  transition: opacity 0.3s ease, transform 0.3s ease;
  pointer-events: none;
  border: 1px solid #e5e7eb;
`;

const PreviewHeader = styled.div`
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PreviewTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const PreviewStatus = styled.div<{ isActive: boolean }>`
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${props => props.isActive ? '#10b981' : '#ef4444'};
  color: white;
  font-weight: 500;
`;

const PreviewContent = styled.div`
  flex: 1;
  height: 240px;
  position: relative;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: white;
  transform: scale(0.75);
  transform-origin: top left;
  width: 133.33%;
  height: 133.33%;
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  color: #6b7280;
  font-size: 12px;
  text-align: center;
  padding: 20px;
`;

interface HoverPreviewProps {
  page: LandingPage;
  show: boolean;
  mouseX: number;
  mouseY: number;
}

export const HoverPreview: React.FC<HoverPreviewProps> = ({ 
  page, 
  show, 
  mouseX, 
  mouseY 
}) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [shouldLoad, setShouldLoad] = useState(false);
  
  const { 
    data: htmlData, 
    isLoading, 
    error 
  } = useGetPageHtmlQuery(page.id, {
    skip: !shouldLoad
  });

  // Delay loading to avoid unnecessary API calls for quick hovers
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    
    if (show) {
      timeout = setTimeout(() => {
        setShouldLoad(true);
      }, 300); // Wait 300ms before loading
    } else {
      setShouldLoad(false);
      setHtmlContent('');
    }
    
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [show]);

  useEffect(() => {
    if (htmlData?.htmlContent) {
      setHtmlContent(htmlData.htmlContent);
    }
  }, [htmlData]);

  const createBlobUrl = (html: string) => {
    const blob = new Blob([html], { type: 'text/html' });
    return URL.createObjectURL(blob);
  };

  if (!show) return null;

  return (
    <PreviewOverlay show={show} mouseX={mouseX} mouseY={mouseY}>
      <PreviewHeader>
        <PreviewTitle>{page.title}</PreviewTitle>
        <PreviewStatus isActive={page.isActive}>
          {page.isActive ? 'Active' : 'Inactive'}
        </PreviewStatus>
      </PreviewHeader>
      <PreviewContent>
        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <ErrorMessage>
            Preview not available
          </ErrorMessage>
        ) : htmlContent ? (
          <PreviewFrame
            src={createBlobUrl(htmlContent)}
            title={`Preview of ${page.title}`}
          />
        ) : (
          <ErrorMessage>
            No preview available
          </ErrorMessage>
        )}
      </PreviewContent>
    </PreviewOverlay>
  );
};